<!--
  Confirms an AI plan draft and takes optional direction from the marketer.

  Exists as a dialog rather than a bare button because a regenerate is destructive to the un-decided half of
  the plan, and that needs saying out loud before it runs.
-->
<template>
  <v-dialog
    v-model="dialog"
    max-width="560"
  >
    <v-card>
      <v-card-title class="text-h6">
        {{ regenerating ? 'Regenerate plan' : 'Build plan' }}
      </v-card-title>

      <v-card-text>
        <p class="text-body-2">
          The plan is drafted from this audience's real numbers — customer mix, print and subscription
          revenue, content coverage, and what's already booked on the calendar.
        </p>

        <v-alert
          v-if="regenerating"
          dense
          text
          type="info"
          class="text-caption"
        >
          Approved items are kept. Dismissed items stay dismissed and won't be proposed again. Only
          undecided suggestions are replaced.
        </v-alert>

        <v-textarea
          v-model="brief"
          outlined
          dense
          auto-grow
          rows="3"
          label="Direction (optional)"
          placeholder="e.g. focus on winning first print orders from free users this quarter"
          hide-details
        />

        <!--
          Pace, stated up front rather than corrected afterwards. Left blank the model picks the pace itself
          from the send caps and the audience size; filled in, these are folded into the direction text, which
          the prompt treats as instructions to follow. Three boxes and not one because email, social and blog
          are limited by completely different things — the same reason the plan states them separately.
        -->
        <div class="text-caption grey--text mt-4 mb-1">
          Pace — leave blank to let the plan work it out
        </div>

        <div class="d-flex">
          <v-text-field
            v-model="emailPace"
            outlined
            dense
            label="Email"
            placeholder="one every 3 weeks"
            hide-details
            class="mr-2"
          />
          <v-text-field
            v-model="socialPace"
            outlined
            dense
            label="Social"
            placeholder="two pins a week"
            hide-details
            class="mr-2"
          />
          <v-text-field
            v-model="blogPosts"
            outlined
            dense
            type="number"
            min="0"
            max="3"
            label="Blog posts"
            hide-details
            class="blog-count"
          />
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="confirm"
        >
          {{ regenerating ? 'Regenerate' : 'Build it' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AudienceBriefDialog',
  props: {
    regenerating: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false,
      brief: '',
      emailPace: '',
      socialPace: '',
      blogPosts: ''
    };
  },
  methods: {
    // Public — the parent opens this through $refs.
    open() {
      this.dialog = true;
    },
    // The pace boxes are appended to the free text as plain instructions, because the direction is one string
    // all the way to the model and the prompt already treats it as the marketer's word. Anything left blank
    // says nothing at all rather than sending an empty target the model would have to interpret.
    composedBrief() {
      const lines = [];
      const email = (this.emailPace || '').trim();
      const social = (this.socialPace || '').trim();
      const posts = parseInt(this.blogPosts, 10);

      if (email) lines.push(`- Email pace: ${email}`);
      if (social) lines.push(`- Social pace: ${social}`);
      // 0 is a real answer — "no blog posts this time" — so it has to survive a falsy check.
      if (Number.isInteger(posts) && posts >= 0) {
        lines.push(`- Blog posts: ${posts}. Draft exactly this many BlogPost items across the whole plan.`);
      }

      const text = (this.brief || '').trim();
      if (!lines.length) return text;

      const pace = ['Hold this plan to the pace below. It is a decision the marketer has already taken, and ' +
        'it OVERRIDES both the pace you would otherwise pick and the send caps in this brief. Plan the pace ' +
        'asked for here even where it runs past a cap or the open slots, and name the cap it runs past in ' +
        'the cadence so the overrun is visible rather than silent.']
        .concat(lines)
        .join('\n');

      return text ? `${text}\n\n${pace}` : pace;
    },
    confirm() {
      this.dialog = false;
      this.$emit('confirm', this.composedBrief());
    }
  }
};
</script>

<style scoped>
/* The count is one digit — the two pace boxes need the width more. */
.blog-count {
  max-width: 120px;
}
</style>
